import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileCheck2, ChevronRight, Loader2, ClipboardList } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { api, errMsg } from "../lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

const GRADE_TONE = {
  "A*": "bg-primary/15 text-primary",
  "A": "bg-primary/10 text-primary",
  "B": "bg-emerald-500/10 text-emerald-500",
  "C": "bg-amber-500/10 text-amber-500",
  "D": "bg-orange-500/10 text-orange-500",
  "E": "bg-orange-500/10 text-orange-500",
  "U": "bg-destructive/10 text-destructive",
};

export default function MySubmissions({ embedded = false, onSwitchToGenerated } = {}) {
  const [subs, setSubs] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = () => {
    setLoadError(null);
    return api.get("/examiner/submissions")
      .then((r) => setSubs(r.data))
      .catch((e) => setLoadError(errMsg(e)));
  };
  useEffect(() => { load(); }, []);

  const body = (
    <>
      {!embedded && (
        <>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Library</p>
          <h1 className="text-4xl font-bold tracking-tighter flex items-center gap-3">
            <ClipboardList className="h-8 w-8 text-primary" /> My Submissions
          </h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">Every paper you've submitted for AI marking. Open one to re-read exactly what you wrote next to why it scored what it did.</p>
        </>
      )}

      {loadError && (
        <div className={`${embedded ? "mt-0" : "mt-10"} rounded-3xl border border-border bg-card p-8 text-center`} data-testid="my-submissions-error">
          <p className="text-sm font-semibold text-destructive mb-2">Couldn't load your submissions</p>
          <p className="text-xs text-muted-foreground mb-6">{loadError}</p>
          <Button data-testid="my-submissions-retry-btn" onClick={load} className="rounded-full glow-btn" size="sm">Retry</Button>
        </div>
      )}

      {!loadError && subs === null && (
        <div className={`${embedded ? "mt-0" : "mt-10"} flex items-center justify-center py-16`} data-testid="my-submissions-loading">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {!loadError && subs && subs.length === 0 && (
        <div className={`${embedded ? "" : "mt-10"} rounded-3xl border border-dashed border-border p-16 text-center`} data-testid="my-submissions-empty">
          <FileCheck2 className="h-6 w-6 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm mb-4">You haven't sat any papers yet.</p>
          {onSwitchToGenerated ? (
            <Button size="sm" className="rounded-full glow-btn" data-testid="my-submissions-empty-cta" onClick={onSwitchToGenerated}>
              Open one from Generated →
            </Button>
          ) : (
            <Button asChild size="sm" className="rounded-full glow-btn" data-testid="my-submissions-empty-cta">
              <Link to="/my-papers">Open one from Generated →</Link>
            </Button>
          )}
        </div>
      )}

      {!loadError && subs && subs.length > 0 && (
        <ul className={`${embedded ? "space-y-3" : "mt-8 space-y-3"}`}>
          {subs.map((s, i) => {
            const pct = s.percentage ?? 0;
            const grade = s.estimated_grade || "-";
            const tone = GRADE_TONE[grade] || "bg-muted text-foreground";
            return (
              <li key={s.id}>
                <Link
                  to={`/submissions/${s.id}`}
                  data-testid={`submission-row-${i}`}
                  className="group flex items-center justify-between gap-4 rounded-2xl border border-border bg-card p-4 hover:border-primary/50 hover:bg-primary/[0.04] transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground truncate">
                      {formatDate(s.created_at)}{s.paper_number ? ` · Paper ${s.paper_number}` : ""}{s.subject ? ` · ${s.subject}` : ""}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold leading-snug group-hover:text-primary transition-colors truncate">
                      {s.total_awarded}/{s.total_available} marks · {pct}%
                    </p>
                  </div>
                  <Badge className={`shrink-0 px-3 py-1 text-xs font-bold ${tone}`}>{grade}</Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:text-primary transition-colors" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );

  if (embedded) return <div data-testid="my-submissions-page">{body}</div>;
  return <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="my-submissions-page">{body}</div>;
}
